import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SettingService } from 'src/app/system-common/services/setting.service';
import * as Chart from 'chart.js';
import { DashboardService } from '../../services/dashboard.service';

@Component({
  selector: '',
  template: '',
})
export class AgentsPerformanceChartHandler {
  chart: Chart;
  @Output() closeChart = new EventEmitter<string>();
  pieColorArray = [];
  frequencyArr = [
    this.settingService.getLabelValue('monthly'),
    this.settingService.getLabelValue('quarterly'),
    this.settingService.getLabelValue('yearly'),
  ];
  frequency = this.settingService.getLabelValue('quarterly');
  selectedCalculationYears: string[] = [];

  public calculationyears;
  private originalRectangleDraw;
  private userLoginData;

  // Performing chart
  performingChartData;
  performingChartOptions;
  public selectedAgent: string = '';
  selectedChartView: 'all' | 'top' | 'bottom' = 'all';
  public agentTopPerformanceGraph: any;
  public isShowPerformingGraph: boolean = false;
  public agentTopPerformanceBarChartData = [];
  public agentNonPerformanceBarChartData = [];

  constructor(public service: DashboardService, public settingService: SettingService) {
    const dateStr = this.service.getDateFormatted(this.settingService.getSessionStorageValue('BatchDate'));
    const year = parseInt(dateStr.split('/')[2], 10);
    this.calculationyears = [String(year), String(year + 1)];
    this.originalRectangleDraw = Chart.elements.Rectangle.prototype.draw;

    if (sessionStorage.getItem('userLoginData') != undefined) {
      this.userLoginData = JSON.parse(sessionStorage.getItem('userLoginData'));
    }
  }

  ngOnInit() {
    this.loadBarChartDetails();
  }

  public loadBarChartDetails() {
    this.pieColorArray = this.service.getColor();
    setTimeout(() => {
      this.loadTopAgentsBarChartDetails();
    }, 10);
  }

  /* Top and Non Performing chart code starts here */

  public loadTopAgentsBarChartDetails() {
    this.isShowPerformingGraph = true;
    this.agentTopPerformanceBarChartData = [];
    this.agentNonPerformanceBarChartData = [];
    this.pieColorArray = this.service.getColor();

    let topAgentsLoaded = false;
    let bottomAgentsLoaded = false;

    this.fetchAgentData('desc')
      .then((data) => {
        this.agentTopPerformanceBarChartData = data['Top Performing Agents'] || {};
        topAgentsLoaded = true;
        if (bottomAgentsLoaded) this.renderAgentsPerformanceChart();
      })
      .catch((error) => {
        console.error('Error fetching Top Performing Agents:', error);
        topAgentsLoaded = true;
        if (bottomAgentsLoaded) this.renderAgentsPerformanceChart();
      });

    this.fetchAgentData('asc')
      .then((data) => {
        this.agentNonPerformanceBarChartData = data['Non Performing Agents'] || {};
        bottomAgentsLoaded = true;
        if (topAgentsLoaded) this.renderAgentsPerformanceChart();
      })
      .catch((error) => {
        console.error('Error fetching Non Performing Agents:', error);
        bottomAgentsLoaded = true;
        if (topAgentsLoaded) this.renderAgentsPerformanceChart();
      });
  }

  // Extracted common fetch logic
  private fetchAgentData(order: 'asc' | 'desc'): Promise<any> {
    return new Promise((resolve, reject) => {
      this.service.getAgentPerformanceData(order).subscribe(
        (data) => {
          try {
            const resData = JSON.parse(JSON.stringify(data));
            resolve(resData['data']['data']);
          } catch {
            reject('Failed to parse response data');
          }
        },
        (error) => reject(error)
      );
    });
  }

  // Render Chart
  private renderAgentsPerformanceChart(showTop: boolean = true, showBottom: boolean = true) {
    const topAgents = this.agentTopPerformanceBarChartData;
    const bottomAgents = this.agentNonPerformanceBarChartData;

    const hasTop = Object.keys(topAgents).length > 0 && showTop;
    const hasBottom = Object.keys(bottomAgents).length > 0 && showBottom;

    const { labels, datasets } = this.prepareBarChartData(topAgents, bottomAgents, hasTop, hasBottom);

    this.performingChartData = { labels, datasets };
    this.performingChartOptions = this.buildBarChartOptions(hasTop, hasBottom);
  }

  // Data Preparation
  private prepareBarChartData(topAgents: any, bottomAgents: any, showTop: boolean, showBottom: boolean) {
    let labels: string[] = [];
    let datasets: any[] = [];

    if (showTop && !showBottom) {
      const { sortedLabels, data } = this.getSortedAgentData(topAgents, 'asc');
      labels = sortedLabels;
      datasets.push(
        this.createDataset('Top Performing Agents', data, 'rgba(0, 200, 83, 0.7)', 'rgba(0, 200, 83, 1)')
      );
    }

    if (!showTop && showBottom) {
      const { sortedLabels, data } = this.getSortedAgentData(bottomAgents, 'desc');
      labels = sortedLabels;
      const negativeData = data.map((val) => -val);
      datasets.push(
        this.createDataset('Non Performing Agents', negativeData, 'rgba(255, 87, 34, 0.7)', 'rgba(255, 87, 34, 1)')
      );
    }

    if (showTop && showBottom) {
      const { sortedLabels: topLabels, data: topData } = this.getSortedAgentData(topAgents, 'asc');
      const { sortedLabels: bottomLabels, data: bottomData } = this.getSortedAgentData(bottomAgents, 'desc');

      const negativeBottomData = bottomData.map((val) => -val);

      labels = [...topLabels, ...bottomLabels];

      datasets.push(
        this.createDataset(
          'Top Performing Agents',
          [...topData, ...new Array(negativeBottomData.length).fill(null)],
          'rgba(0, 200, 83, 0.7)',
          'rgba(0, 200, 83, 1)'
        )
      );

      datasets.push(
        this.createDataset(
          'Non Performing Agents',
          [...new Array(topData.length).fill(null), ...negativeBottomData],
          'rgba(255, 87, 34, 0.7)',
          'rgba(255, 87, 34, 1)'
        )
      );
    }

    return { labels, datasets };
  }

  // Sorted Agent Data Helper
  private getSortedAgentData(agentData: Record<string, number>, order: 'asc' | 'desc') {
    const sorted = Object.entries(agentData).sort((a, b) => (order === 'asc' ? a[1] - b[1] : b[1] - a[1]));
    return {
      sortedLabels: sorted.map(([label]) => label),
      data: sorted.map(([_, val]) => val),
    };
  }

  // Dataset Factory
  private createDataset(label: string, data: number[], backgroundColor: string, borderColor: string) {
    return {
      label,
      backgroundColor,
      borderColor,
      data,
      barPercentage: data.length > 1 ? 0.8 : 0.5,
      categoryPercentage: data.length > 1 ? 0.4 : 0.5,
    };
  }

  // Chart Options Builder
  private buildBarChartOptions(showTop: boolean, showBottom: boolean) {
    const commonFontSettings = {
      fontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
      fontColor: '#000000',
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 20, right: 20, top: 20, bottom: 20 } },
      tooltips: {
        mode: 'index',
        intersect: false,
        bodyFontFamily: commonFontSettings.fontFamily,
        filter: (tooltipItem, data) => {
          const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          return value !== null;
        },
        callbacks: {
          label: (tooltipItem, data) => {
            const val = Math.abs(tooltipItem.yLabel);
            return `${data.datasets[tooltipItem.datasetIndex].label}: ${val.toLocaleString()}`;
          },
        },
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: (value) => Math.abs(value).toLocaleString(),
              ...commonFontSettings,
            },
          },
        ],
        xAxes: [
          {
            stacked: showTop && showBottom,
            ticks: {
              fontSize: 12,
              ...commonFontSettings,
            },
          },
        ],
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          fontSize: 12,
          ...commonFontSettings,
        },
      },
      plugins: {
        datalabels: false,
      },
    };
  }

  // Count Getters
  get topAgentCount(): number {
    return this.agentTopPerformanceBarChartData && typeof this.agentTopPerformanceBarChartData === 'object'
      ? Object.keys(this.agentTopPerformanceBarChartData).length
      : 0;
  }

  get bottomAgentCount(): number {
    return this.agentNonPerformanceBarChartData && typeof this.agentNonPerformanceBarChartData === 'object'
      ? Object.keys(this.agentNonPerformanceBarChartData).length
      : 0;
  }

  /* Filtering Method */
  filterAgents(view: 'all' | 'top' | 'bottom') {
    this.selectedChartView = view;
    this.isShowPerformingGraph = false;
    this.performingChartData = null;
    this.performingChartOptions = null;

    setTimeout(() => {
      this.isShowPerformingGraph = true;

      const showTop = view === 'all' || view === 'top';
      const showBottom = view === 'all' || view === 'bottom';

      this.renderAgentsPerformanceChart(showTop, showBottom);
    }, 10);
  }

  /* Filter Code For Performing Ends Here */

  // Close the Chart on click of the close button
  onCloseClick() {
    this.closeChart.emit(this.chart);
  }

  ngOnDestroy() {
    this.service.clear();
    Chart.elements.Rectangle.prototype.draw = this.originalRectangleDraw;
  }
}
